import React, { useRef, useEffect, useState, useCallback } from "react"
import { debounce } from "@utils/debounce"

import "@styles/components/ticker-tape.scss"

const MAX_ANIMATION_DURATION = 120
const DEFAULT_ANIMATION_DURATION = 30
const RESIZE_DEBOUNCE_MS = 150
const PIXELS_PER_SECOND = 20
const MIN_DUPLICATES = 1

/**
 * TickerTape component that displays a horizontally scrolling ticker with multiple items.
 * Automatically calculates animation duration to maintain constant scroll speed and
 * duplicates content according to the window width to create a seamless infinite loop effect.
 */
const TickerTape = ({ slice, ariaLabel = "Oddbox ticker messages" }) => {
  const tickerItems = slice.items.map(item => item.item.text).filter(Boolean)
  // Reference to the first content div so we can measure its width
  const contentRef = useRef(null)
  const windowWidthRef = useRef(
    typeof window !== "undefined" ? window.innerWidth : 0,
  )

  const [duplicates, setDuplicates] = useState(MIN_DUPLICATES)
  const [animationDuration, setAnimationDuration] = useState(
    DEFAULT_ANIMATION_DURATION,
  )

  const updateDuplicatesAndDuration = useCallback(() => {
    if (!contentRef.current) return

    const contentWidth = contentRef.current.offsetWidth
    const windowWidth = windowWidthRef.current

    if (!contentWidth || !windowWidth) {
      setDuplicates(MIN_DUPLICATES)
      setAnimationDuration(DEFAULT_ANIMATION_DURATION)
      return
    }

    // Determine animation duration
    const calculatedDuration = contentWidth / PIXELS_PER_SECOND
    const newDuration = Math.min(MAX_ANIMATION_DURATION, calculatedDuration)

    // Calculate ticker duplicates
    const minCopies = Math.ceil(windowWidth / contentWidth) + 1 // +1 for seamless loop

    setDuplicates(minCopies)
    setAnimationDuration(newDuration)
  }, [])

  useEffect(() => {
    // Measure items and determine duplicates and animation duration after initial render and DOM is ready
    const rafId = requestAnimationFrame(() => {
      updateDuplicatesAndDuration()
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [updateDuplicatesAndDuration])

  useEffect(() => {
    // Add debounced resize event listener
    let rafId
    const debouncedHandleResize = debounce(() => {
      windowWidthRef.current = window.innerWidth
      rafId = requestAnimationFrame(() => {
        updateDuplicatesAndDuration()
      })
    }, RESIZE_DEBOUNCE_MS)

    if (typeof window !== "undefined") {
      window.addEventListener("resize", debouncedHandleResize)
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", debouncedHandleResize)
        debouncedHandleResize.cancel()
      }
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [updateDuplicatesAndDuration])

  if (!tickerItems?.length) {
    return null
  }

  return (
    <div className="ticker" role="region" aria-label={ariaLabel}>
      <div className="ticker__track">
        {Array.from({ length: duplicates }).map((_, duplicationIndex) => (
          <div
            ref={duplicationIndex === 0 ? contentRef : null}
            className="ticker__content"
            aria-hidden={duplicationIndex === 0 ? undefined : true}
            key={`ticker-duplication-${duplicationIndex}-${duplicates}`}
            style={{
              animationDuration: `${animationDuration}s`,
            }}
          >
            {tickerItems.map((item, index) => (
              <div
                className="ticker__item"
                key={`ticker-item-${duplicationIndex}-${index}`}
              >
                {item}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default TickerTape
